/**
 * Industry Tabs Block - Frontend JavaScript
 *
 * Implements WAI-ARIA tabs pattern with automatic activation
 * and select-only combobox for mobile.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
 */

(function () {
	function initTabs() {
		const tabContainers = document.querySelectorAll('.commcore26-tabs');

		tabContainers.forEach((container) => {
			// Skip if already initialized
			if (container.dataset.initialized) return;
			container.dataset.initialized = 'true';

			const tablist = container.querySelector('[role="tablist"]');
			const tabs = container.querySelectorAll('[role="tab"]');
			const panels = container.querySelectorAll('[role="tabpanel"]');

			// Combobox elements
			const combobox = container.querySelector('.commcore26-tabs__combobox');
			const listbox = container.querySelector('.commcore26-tabs__listbox');
			const options = container.querySelectorAll('.commcore26-tabs__option');
			const comboboxValue = container.querySelector('.commcore26-tabs__combobox-value');

			if (!tablist || tabs.length === 0) return;

			let activeOptionIndex = parseInt(combobox?.dataset.value || '0', 10);

			/**
			 * Activate a tab by index and show its panel
			 * @param {number} index - The tab index to activate
			 */
			function activateTabByIndex(index) {
				const tab = tabs[index];
				if (!tab) return;

				// Deactivate all tabs
				tabs.forEach((t) => {
					t.setAttribute('aria-selected', 'false');
					t.setAttribute('tabindex', '-1');
					t.classList.remove('is-active');
				});

				// Hide all panels
				panels.forEach((panel) => {
					panel.hidden = true;
					panel.classList.remove('is-active');
				});

				// Activate selected tab
				tab.setAttribute('aria-selected', 'true');
				tab.setAttribute('tabindex', '0');
				tab.classList.add('is-active');

				// Show corresponding panel
				const panelId = tab.getAttribute('aria-controls');
				const panel = document.getElementById(panelId);
				if (panel) {
					panel.hidden = false;
					panel.classList.add('is-active');
				}

				// Sync combobox
				if (combobox && options[index]) {
					combobox.dataset.value = index;
					comboboxValue.textContent = options[index].textContent;
					options.forEach((opt, i) => {
						opt.classList.toggle('is-selected', i === index);
						opt.setAttribute('aria-selected', i === index ? 'true' : 'false');
					});
				}
			}

			/**
			 * Focus a tab (and activate it for automatic activation pattern)
			 * @param {number} index - The tab index to focus
			 */
			function focusTab(index) {
				const tab = tabs[index];
				if (!tab) return;
				tab.focus();
				activateTabByIndex(index);
			}

			// ===== TABS FUNCTIONALITY =====

			// Add click handlers to tabs
			tabs.forEach((tab, index) => {
				tab.addEventListener('click', () => {
					activateTabByIndex(index);
				});
			});

			// Add keyboard navigation to tablist
			tablist.addEventListener('keydown', (e) => {
				const currentTab = document.activeElement;
				const currentIndex = Array.from(tabs).indexOf(currentTab);

				if (currentIndex === -1) return;

				let handled = false;

				switch (e.key) {
					case 'ArrowRight':
						focusTab((currentIndex + 1) % tabs.length);
						handled = true;
						break;
					case 'ArrowLeft':
						focusTab((currentIndex - 1 + tabs.length) % tabs.length);
						handled = true;
						break;
					case 'Home':
						focusTab(0);
						handled = true;
						break;
					case 'End':
						focusTab(tabs.length - 1);
						handled = true;
						break;
				}

				if (handled) {
					e.preventDefault();
					e.stopPropagation();
				}
			});

			// ===== COMBOBOX FUNCTIONALITY =====

			if (!combobox || !listbox) return;

			let isOpen = false;

			function openListbox() {
				isOpen = true;
				combobox.setAttribute('aria-expanded', 'true');
				listbox.classList.add('is-open');

				// Set active descendant to current selection
				const currentOption = options[activeOptionIndex];
				if (currentOption) {
					combobox.setAttribute('aria-activedescendant', currentOption.id);
					currentOption.classList.add('is-focused');
					currentOption.scrollIntoView({ block: 'nearest' });
				}
			}

			function closeListbox(restoreFocus = true) {
				isOpen = false;
				combobox.setAttribute('aria-expanded', 'false');
				combobox.setAttribute('aria-activedescendant', '');
				listbox.classList.remove('is-open');

				options.forEach((opt) => opt.classList.remove('is-focused'));

				if (restoreFocus) {
					combobox.focus();
				}
			}

			function selectOption(index) {
				if (index < 0 || index >= options.length) return;

				activeOptionIndex = index;
				activateTabByIndex(index);
				closeListbox();
			}

			function focusOption(index) {
				if (index < 0 || index >= options.length) return;

				// Remove focus from all options
				options.forEach((opt) => opt.classList.remove('is-focused'));

				// Focus the new option
				const option = options[index];
				option.classList.add('is-focused');
				combobox.setAttribute('aria-activedescendant', option.id);
				option.scrollIntoView({ block: 'nearest' });

				activeOptionIndex = index;
			}

			// Click to toggle listbox
			combobox.addEventListener('click', () => {
				if (isOpen) {
					closeListbox();
				} else {
					openListbox();
				}
			});

			// Click on option
			options.forEach((option, index) => {
				option.addEventListener('click', (e) => {
					e.stopPropagation();
					selectOption(index);
				});
			});

			// Close on outside click
			document.addEventListener('click', (e) => {
				if (isOpen && !container.contains(e.target)) {
					closeListbox(false);
				}
			});

			// Keyboard navigation
			combobox.addEventListener('keydown', (e) => {
				const { key } = e;

				if (!isOpen) {
					// Closed state
					switch (key) {
						case 'ArrowDown':
						case 'ArrowUp':
						case 'Enter':
						case ' ':
							e.preventDefault();
							openListbox();
							break;
						case 'Home':
							e.preventDefault();
							activeOptionIndex = 0;
							openListbox();
							break;
						case 'End':
							e.preventDefault();
							activeOptionIndex = options.length - 1;
							openListbox();
							break;
						default:
							// Type-ahead: find option starting with typed character
							if (key.length === 1 && key.match(/\S/)) {
								const char = key.toLowerCase();
								const matchIndex = Array.from(options).findIndex((opt) =>
									opt.textContent.trim().toLowerCase().startsWith(char)
								);
								if (matchIndex !== -1) {
									activeOptionIndex = matchIndex;
									openListbox();
								}
							}
					}
				} else {
					// Open state
					switch (key) {
						case 'ArrowDown':
							e.preventDefault();
							focusOption(Math.min(activeOptionIndex + 1, options.length - 1));
							break;
						case 'ArrowUp':
							e.preventDefault();
							focusOption(Math.max(activeOptionIndex - 1, 0));
							break;
						case 'Home':
							e.preventDefault();
							focusOption(0);
							break;
						case 'End':
							e.preventDefault();
							focusOption(options.length - 1);
							break;
						case 'PageUp':
							e.preventDefault();
							focusOption(Math.max(activeOptionIndex - 10, 0));
							break;
						case 'PageDown':
							e.preventDefault();
							focusOption(Math.min(activeOptionIndex + 10, options.length - 1));
							break;
						case 'Enter':
						case ' ':
							e.preventDefault();
							selectOption(activeOptionIndex);
							break;
						case 'Escape':
							e.preventDefault();
							closeListbox();
							break;
						case 'Tab':
							selectOption(activeOptionIndex);
							break;
						default:
							// Type-ahead in open state
							if (key.length === 1 && key.match(/\S/)) {
								const char = key.toLowerCase();
								const matchIndex = Array.from(options).findIndex((opt) =>
									opt.textContent.trim().toLowerCase().startsWith(char)
								);
								if (matchIndex !== -1) {
									focusOption(matchIndex);
								}
							}
					}
				}
			});

			// Close on blur (with delay to allow click on option)
			combobox.addEventListener('blur', () => {
				setTimeout(() => {
					if (isOpen && !listbox.contains(document.activeElement)) {
						closeListbox(false);
					}
				}, 150);
			});
		});
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTabs);
	} else {
		initTabs();
	}
})();
